import { createContext, useContext, useState, useEffect } from 'react';
import {
  BUSINESS_STATE, BUSINESS_DETAILS, SHIPPING_FLAT, FREE_SHIPPING_THRESHOLD,
  DEFAULT_GST_RATE, parseRate
} from '../lib/storeConfig';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Commercial rules come from the admin Settings screen via GET /api/settings.
 *
 * The constants in storeConfig are only fallbacks for the first paint and for the
 * case where the request fails — the storefront must never show a different shipping
 * charge from the one an admin just saved, so the API always wins once it responds.
 */
const FALLBACK = {
  shipping_flat: SHIPPING_FLAT,
  free_shipping_threshold: FREE_SHIPPING_THRESHOLD,
  cod_enabled: true,
  cod_fee: 0,
  business_state: BUSINESS_STATE,
  default_gst_rate: DEFAULT_GST_RATE,
  business: BUSINESS_DETAILS
};

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API}/settings`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && data) {
          // Merge rather than replace: a partial response should not wipe defaults.
          setSettings((prev) => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Falling back to built-in store settings:', error.message);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const shippingFlat = Number(settings.shipping_flat) || 0;
  const freeThreshold = Number(settings.free_shipping_threshold) || 0;

  /** Shipping charge for a given order subtotal. */
  const shippingFor = (subtotal) => {
    const value = Number(subtotal) || 0;
    if (value <= 0) return 0;                    // empty cart owes nothing
    return value >= freeThreshold ? 0 : shippingFlat;
  };

  const isIntraState = (state) =>
    !!state && state.trim().toLowerCase() === String(settings.business_state || '').toLowerCase();

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loaded,
        shippingFlat,
        freeThreshold,
        codEnabled: settings.cod_enabled !== false,
        codFee: Number(settings.cod_fee) || 0,
        // `?? DEFAULT_GST_RATE`, not `|| DEFAULT_GST_RATE`: an admin who sets the
        // store default to 0 means 0, and `||` used to turn that back into 18%.
        defaultGstRate: parseRate(settings.default_gst_rate) ?? DEFAULT_GST_RATE,
        businessState: settings.business_state || BUSINESS_STATE,
        // Field-by-field merge: the API sends the whole object, but a blank field
        // in it must not wipe the printed header down to an empty line.
        business: Object.fromEntries(
          Object.entries(BUSINESS_DETAILS).map(([key, fallback]) => {
            const saved = settings.business?.[key];
            return [key, saved === undefined || saved === null || saved === '' ? fallback : saved];
          })
        ),
        shippingFor,
        isIntraState
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
};
